import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateFaDecreaseOriginalAmountMutation,
  useGetAssetRegistrationQuery,
  useGetCurrentAssetValueInfoLazyQuery,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  useUpdateFaDecreaseOriginalAmountMutation,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'

interface DecreaseOriginalValueFormData {
  asset_no: any
  asset_name: string
  start_date: Date | null
  model_no: string
  old_original_value: number
  old_net_book_value: number
  change_date: Date
  decrease_amount: number
  new_original_value: number
  new_net_book_value: number
  description: string
  reason: string
}

interface CurrentAssetValueInfo {
  baseOriginalAmount: number
  openingAccumulatedDepreciation: number
  totalIncreaseOriginalValue: number
  totalDecreaseOriginalValue: number
  generatedDepreciation: number
  totalDepreciationAdjustment: number
  totalProvisionImpairment: number
  totalReversalImpairment: number
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentNetBookValue: number
  quantity: number
  residualValue: number
  usefulLife: number
}

const safeParseNumber = (value: any): number => {
  if (value === undefined || value === null || value === '') return 0
  const parsed =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const DecreaseOriginalValueForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(null)
  const [
    currentAssetInfo,
    setCurrentAssetInfo,
  ] = useState<CurrentAssetValueInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  const decreaseOriginalValueSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    change_date: yup.date().required('Change Date is required'),
    decrease_amount: yup
      .number()
      .typeError('Decrease Amount is required')
      .required('Decrease Amount is required')
      .moreThan(0, 'Decrease Amount must be greater than 0'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const {
    register,
    setValue,
    control,
    handleSubmit,
    watch,
    getValues,
    errors,
  } = useForm<DecreaseOriginalValueFormData>({
    defaultValues: {
      change_date: editData?.change_date
        ? new Date(editData.change_date)
        : new Date(),
      old_original_value: editData?.old_original_value || 0,
      decrease_amount: editData?.decrease_amount || 0,
      new_original_value: editData?.new_original_value || 0,
      old_net_book_value: editData?.old_net_book_value || 0,
      new_net_book_value: editData?.new_net_book_value || 0,
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(decreaseOriginalValueSchema),
  })

  const watchDecreaseAmount = watch('decrease_amount')

  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [editDataLoaded, setEditDataLoaded] = useState(false)

  const [
    getCurrentAssetValueInfo,
    { loading: assetValueLoading },
  ] = useGetCurrentAssetValueInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentAssetValueInfo) {
        const info = data.getCurrentAssetValueInfo
        setCurrentAssetInfo(info as CurrentAssetValueInfo)

        setValue('old_original_value', info.currentOriginalValue)
        setValue('old_net_book_value', info.currentNetBookValue)

        const isEditMode = formMode === 'edit' || formMode === 'resubmit'
        if (isEditMode && editData?.decrease_amount && !editDataLoaded) {
          const existingDecreaseAmount = safeParseNumber(
            editData.decrease_amount
          )
          setValue('decrease_amount', existingDecreaseAmount)
          setValue(
            'new_original_value',
            info.currentOriginalValue - existingDecreaseAmount
          )
          setValue(
            'new_net_book_value',
            Math.max(0, info.currentNetBookValue - existingDecreaseAmount)
          )
          setEditDataLoaded(true)
        } else if (!isEditMode) {
          setValue('decrease_amount', 0)
          setValue('new_original_value', info.currentOriginalValue)
          setValue('new_net_book_value', info.currentNetBookValue)
        }
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  const [
    createDecreaseOriginalAmount,
    { loading: createLoading },
  ] = useCreateFaDecreaseOriginalAmountMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/decrease-original-value`
      )
    },
  })

  const [
    updateDecreaseOriginalAmount,
    { loading: updateLoading },
  ] = useUpdateFaDecreaseOriginalAmountMutation({
    onError: ({ message }) => {
      const error = message?.startsWith('GraphQL error: ')
        ? message.substring(15)
        : message
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(
        `/fixed-assets/${CompanyID}/assets-change/decrease-original-value`
      )
    },
  })

  useEffect(() => {
    if (currentAssetInfo) {
      const decreaseAmount = safeParseNumber(watchDecreaseAmount)
      const currentOriginalValue = currentAssetInfo.currentOriginalValue
      const currentNetBookValue = currentAssetInfo.currentNetBookValue

      const newOriginalValue = Math.max(
        0,
        currentOriginalValue - decreaseAmount
      )
      const newNetBookValue = Math.max(0, currentNetBookValue - decreaseAmount)

      setValue('new_original_value', newOriginalValue)
      setValue('new_net_book_value', newNetBookValue)
    }
  }, [watchDecreaseAmount, currentAssetInfo, setValue])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue(
          'start_date',
          asset.start_use_date ? new Date(asset.start_use_date) : null
        )
        setValue('model_no', asset.model_no || '')

        if (editData.change_date) {
          setValue('change_date', new Date(editData.change_date))
        }
        if (editData.description) {
          setValue('description', editData.description)
        }
        if (editData.reason) {
          setValue('reason', editData.reason)
        }

        getCurrentAssetValueInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_decrease_id: editData.decrease_original_value_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  const handleAssetChange = (asset: any) => {
    if (asset) {
      setSelectedAsset(asset)
      setValue('asset_no', asset)
      setValue('asset_name', asset.name || '')
      setValue(
        'start_date',
        asset.start_use_date ? new Date(asset.start_use_date) : null
      )
      setValue('model_no', asset.model_no || '')

      getCurrentAssetValueInfo({
        variables: {
          fa_asset_id: asset.fa_asset_id,
          exclude_decrease_id: editData?.decrease_original_value_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setCurrentAssetInfo(null)
      setValue('asset_no', null)
      setValue('old_original_value', 0)
      setValue('old_net_book_value', 0)
      setValue('decrease_amount', 0)
      setValue('new_original_value', 0)
      setValue('new_net_book_value', 0)
    }
  }

  const onSubmit = (
    data: DecreaseOriginalValueFormData,
    approvalStatus: ApprovalStatus
  ) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The change date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    if (!currentAssetInfo) {
      setErrMsg('Please wait for asset information to load')
      setErrorDia(true)
      return
    }

    const decreaseAmount = safeParseNumber(data.decrease_amount)
    const currentOriginalValue = currentAssetInfo.currentOriginalValue

    if (decreaseAmount <= 0) {
      setErrMsg('Decrease amount must be greater than 0')
      setErrorDia(true)
      return
    }

    if (decreaseAmount > currentOriginalValue) {
      setErrMsg(
        `Decrease amount (${formatCurrency(
          decreaseAmount
        )}) cannot exceed current original value (${formatCurrency(
          currentOriginalValue
        )}).`
      )
      setErrorDia(true)
      return
    }

    const input = {
      decrease_original_value_id:
        editData?.decrease_original_value_id || undefined,
      fa_asset_id: currentAsset.fa_asset_id,
      company_id: CompanyID,
      old_original_value: currentAssetInfo.currentOriginalValue,
      decrease_amount: decreaseAmount,
      new_original_value:
        currentAssetInfo.currentOriginalValue - decreaseAmount,
      old_net_book_value: currentAssetInfo.currentNetBookValue,
      new_net_book_value: Math.max(
        0,
        currentAssetInfo.currentNetBookValue - decreaseAmount
      ),
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createDecreaseOriginalAmount({ variables: { input } })
    } else {
      updateDecreaseOriginalAmount({ variables: { input } })
    }
  }

  const watchChangeDate =
    watch('change_date') || editData?.change_date || new Date()
  const changeDateStr = (() => {
    const d = new Date(watchChangeDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchChangeDate)
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        createLoading ||
        updateLoading ||
        assetValueLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/decrease-original-value`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Decrease in Original Value', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents>
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={props => (
                    <div>
                      <div>
                        <span className="xsTitle">{props?.DocNo}</span>
                        <span className="desc" style={{ marginLeft: '8px' }}>
                          {props?.name}
                        </span>
                      </div>
                    </div>
                  )}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      label="Asset"
                      style={{ width: '100%' }}
                      required
                      margin="normal"
                      helperText={errors?.asset_no?.message}
                      error={errors?.asset_no ? true : false}
                    />
                  )}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Asset Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={
                editData?.Asset?.start_use_date
                  ? new Date(editData.Asset.start_use_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Original Value"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                helperText={
                  currentAssetInfo
                    ? `Base: ${formatCurrency(
                        currentAssetInfo.baseOriginalAmount
                      )} | +Increase: ${formatCurrency(
                        currentAssetInfo.totalIncreaseOriginalValue
                      )} | -Decrease: ${formatCurrency(
                        currentAssetInfo.totalDecreaseOriginalValue
                      )}`
                    : ''
                }
              />
            )}
            name="old_original_value"
            control={control}
            defaultValue={editData?.old_original_value || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Net Book Value"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                helperText={
                  currentAssetInfo
                    ? `Acc. Dep: ${formatCurrency(
                        currentAssetInfo.currentAccumulatedDepreciation
                      )}`
                    : ''
                }
              />
            )}
            name="old_net_book_value"
            control={control}
            defaultValue={editData?.old_net_book_value || 0}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Decrease in Original Value' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Change Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="change_date"
              control={control}
              defaultValue={
                editData?.change_date
                  ? new Date(editData.change_date)
                  : new Date()
              }
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => onChange(floatValue || 0)}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Decrease Amount"
                fullWidth
                margin="dense"
                required
                disabled={mode === 'approve-reject'}
                helperText={
                  errors?.decrease_amount?.message ||
                  (currentAssetInfo
                    ? `Max: ${formatCurrency(
                        currentAssetInfo.currentOriginalValue
                      )}`
                    : '')
                }
                error={errors?.decrease_amount ? true : false}
              />
            )}
            name="decrease_amount"
            control={control}
            defaultValue={editData?.decrease_amount || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current New Original Value"
                fullWidth
                margin="dense"
                className="left"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="new_original_value"
            control={control}
            defaultValue={editData?.new_original_value || 0}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current New Net Book Value"
                fullWidth
                margin="dense"
                className="right"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="new_net_book_value"
            control={control}
            defaultValue={editData?.new_net_book_value || 0}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/decrease-original-value`
          )
        }}
      />
    </>
  )
}
